import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import styled from 'styled-components';
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore — react-icons + React 19 typings
import {
  FaArrowLeft as FaArrowLeftRaw,
  FaDownload as FaDownloadRaw,
  FaEllipsisH as FaEllipsisHRaw,
  FaFilter as FaFilterRaw,
  FaPlus as FaPlusRaw,
  FaSearch as FaSearchRaw,
  FaCheck as FaCheckRaw,
  FaPaperPlane as FaPaperPlaneRaw,
} from 'react-icons/fa';
import { teacherService, type TeacherGradeRow } from '../../services/classroom/teacherService';
import ClassSectionSidebar from '../../components/classroom/ClassSectionSidebar';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const FaArrowLeft: any = FaArrowLeftRaw;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const FaDownload: any = FaDownloadRaw;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const FaEllipsisH: any = FaEllipsisHRaw;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const FaFilter: any = FaFilterRaw;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const FaPlus: any = FaPlusRaw;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const FaSearch: any = FaSearchRaw;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const FaCheck: any = FaCheckRaw;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const FaPaperPlane: any = FaPaperPlaneRaw;

const MOBILE_BREAKPOINT = '1024px';

const Page = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
  min-width: 0;
`;

const BackRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
`;

const BackLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  font-size: 0.86rem;
  font-weight: 600;
  color: var(--primary);
  text-decoration: none;
  &:hover {
    text-decoration: underline;
  }
`;

const CourseBanner = styled.section<{ $imageUrl?: string | null }>`
  border-radius: 16px;
  border: 1px solid var(--outline);
  padding: 1.75rem 1.85rem 1.5rem;
  color: var(--onPrimary);
  box-shadow: 0 8px 28px rgba(0, 0, 0, 0.2);
  min-height: 140px;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  background: ${({ $imageUrl }) => {
    const base =
      'linear-gradient(135deg, var(--primaryDark) 0%, var(--primary) 55%, color-mix(in srgb, var(--primary) 70%, var(--surface)) 100%)';
    const url = typeof $imageUrl === 'string' ? $imageUrl.trim() : '';
    if (!url) return base;
    return `linear-gradient(to top, rgba(0,0,0,0.72) 0%, rgba(0,0,0,0.35) 45%, rgba(0,0,0,0.15) 100%), url(${url}) center / cover no-repeat, ${base}`;
  }};
`;

const BannerKicker = styled.p`
  margin: 0 0 0.35rem;
  font-size: 0.7rem;
  font-weight: 700;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  opacity: 0.9;
`;

const BannerTitle = styled.h1`
  margin: 0 0 0.45rem;
  font-family: 'Poppins', sans-serif;
  font-size: clamp(1.35rem, 3vw, 1.85rem);
  font-weight: 700;
  letter-spacing: 0.02em;
  text-transform: uppercase;
  line-height: 1.15;
`;

const BannerSub = styled.p`
  margin: 0;
  font-size: 0.88rem;
  opacity: 0.88;
  font-weight: 500;
`;

const LayoutGrid = styled.div`
  display: grid;
  grid-template-columns: 232px minmax(0, 1fr);
  gap: 1.35rem;
  align-items: start;
  @media (max-width: ${MOBILE_BREAKPOINT}) {
    grid-template-columns: 1fr;
  }
`;

const RosterCard = styled.div`
  margin: 0.75rem 0.5rem 0;
  padding: 0.85rem 0.75rem;
  border-radius: 12px;
  background: var(--surfaceVariant);
  border: 1px solid var(--outline);
`;

const RosterLabel = styled.div`
  font-size: 0.68rem;
  font-weight: 700;
  letter-spacing: 0.06em;
  color: var(--textSecondary, var(--onSurfaceVariant));
  text-transform: uppercase;
  margin-bottom: 0.35rem;
`;

const RosterValue = styled.div`
  font-family: 'Poppins', sans-serif;
  font-size: 1.35rem;
  font-weight: 700;
  color: var(--text);
`;

const DownloadRosterBtn = styled.button`
  margin-top: 0.75rem;
  width: 100%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.4rem;
  padding: 0.55rem 0.65rem;
  border-radius: 10px;
  border: 1px solid var(--outline);
  background: var(--surface);
  font-size: 0.78rem;
  font-weight: 600;
  color: var(--text);
  cursor: pointer;
  font-family: inherit;
  &:hover {
    border-color: var(--primary);
    color: var(--primary);
  }
`;

const MainCol = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  min-width: 0;
`;

const ToolbarRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.65rem;
`;

const SearchWrap = styled.div`
  flex: 1;
  min-width: 200px;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 0.85rem;
  border-radius: 12px;
  border: 1px solid var(--outline);
  background: var(--surface);
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.06);
`;

const SearchInput = styled.input`
  flex: 1;
  min-width: 0;
  border: none;
  background: transparent;
  font-size: 0.88rem;
  font-family: inherit;
  color: var(--text);
  outline: none;
  &::placeholder {
    color: var(--textSecondary, var(--onSurfaceVariant));
  }
`;

const IconToolBtn = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 42px;
  height: 42px;
  border-radius: 12px;
  border: 1px solid var(--outline);
  background: var(--surface);
  color: var(--textSecondary, var(--onSurfaceVariant));
  cursor: pointer;
  &:hover {
    border-color: var(--primary);
    color: var(--primary);
  }
`;

const AddStudentsBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.42rem;
  padding: 0.6rem 0.85rem;
  border-radius: 12px;
  border: 1px solid var(--outline);
  background: var(--surface);
  color: var(--text);
  cursor: pointer;
  font-size: 0.8rem;
  font-weight: 700;
  font-family: inherit;
  &:hover {
    border-color: var(--primary);
    color: var(--primary);
  }
`;

const MessageAllBtn = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 0.42rem;
  padding: 0.6rem 0.85rem;
  border-radius: 12px;
  border: 1px solid var(--primary);
  background: var(--primary);
  color: var(--onPrimary);
  cursor: pointer;
  font-size: 0.8rem;
  font-weight: 700;
  font-family: inherit;
  text-decoration: none;
  &:hover {
    opacity: 0.92;
  }
  &[aria-disabled='true'] {
    opacity: 0.55;
    pointer-events: none;
  }
`;

const PillsRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  align-items: center;
  margin-left: auto;
`;

const SummaryPill = styled.span<{ $tone: 'primary' | 'warning' }>`
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  padding: 0.45rem 0.85rem;
  border-radius: 999px;
  font-size: 0.78rem;
  font-weight: 700;
  ${({ $tone }) =>
    $tone === 'primary'
      ? `
    background: color-mix(in srgb, var(--primary) 18%, transparent);
    color: var(--primary);
    border: 1px solid color-mix(in srgb, var(--primary) 35%, var(--outline));
  `
      : `
    background: color-mix(in srgb, var(--warning, #e8a23c) 20%, transparent);
    color: var(--warning, #fbbf24);
    border: 1px solid color-mix(in srgb, var(--warning, #e8a23c) 40%, var(--outline));
  `}
`;

const EnrollPanel = styled.section`
  border: 1px solid var(--outline);
  border-radius: 14px;
  background: var(--surface);
  padding: 0.9rem;
  box-shadow: 0 1px 8px rgba(0, 0, 0, 0.06);
`;

const EnrollPanelTop = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  margin-bottom: 0.65rem;
`;

const EnrollPanelTitle = styled.h3`
  margin: 0;
  font-size: 0.92rem;
  font-weight: 700;
  color: var(--text);
`;

const EnrollCount = styled.span`
  font-size: 0.76rem;
  font-weight: 700;
  color: var(--textSecondary, var(--onSurfaceVariant));
`;

const CandidateList = styled.div`
  margin-top: 0.65rem;
  border: 1px solid var(--outline);
  border-radius: 12px;
  background: var(--surfaceVariant);
  max-height: 260px;
  overflow: auto;
`;

const CandidateRow = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  gap: 0.65rem;
  align-items: center;
  padding: 0.6rem 0.75rem;
  background: var(--surface);
  &:not(:last-child) {
    border-bottom: 1px solid var(--outline);
  }
`;

const CandidateMain = styled.div`
  display: grid;
  grid-template-columns: 44px minmax(0, 1fr);
  gap: 0.55rem;
  align-items: center;
  min-width: 0;
`;

const CandidateName = styled.div`
  font-size: 0.86rem;
  font-weight: 700;
  color: var(--text);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const CandidateEmail = styled.div`
  margin-top: 0.08rem;
  font-size: 0.76rem;
  color: var(--textSecondary, var(--onSurfaceVariant));
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const CandidateToggleBtn = styled.button<{ $selected: boolean }>`
  width: 36px;
  height: 36px;
  border-radius: 10px;
  border: 1px solid ${({ $selected }) => ($selected ? 'var(--primary)' : 'var(--outline)')};
  background: ${({ $selected }) => ($selected ? 'var(--primary)' : 'var(--surface)')};
  color: ${({ $selected }) => ($selected ? 'var(--onPrimary)' : 'var(--textSecondary, var(--onSurfaceVariant))')};
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  flex-shrink: 0;
  &:hover {
    border-color: var(--primary);
    color: ${({ $selected }) => ($selected ? 'var(--onPrimary)' : 'var(--primary)')};
  }
`;

const EnrollActions = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: 0.65rem;
`;

const EnrollSubmitBtn = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 0;
  border-radius: 10px;
  padding: 0.62rem 1rem;
  background: var(--primary);
  color: var(--onPrimary);
  font-size: 0.82rem;
  font-weight: 700;
  cursor: pointer;
  font-family: inherit;
  &:disabled {
    opacity: 0.65;
    cursor: not-allowed;
  }
`;

const InlineStatus = styled.p<{ $tone: 'error' | 'success' }>`
  margin: 0.35rem 0 0;
  font-size: 0.8rem;
  color: ${({ $tone }) => ($tone === 'error' ? 'var(--error, #b91c1c)' : 'var(--success, #15803d)')};
`;

const TableCard = styled.div`
  border: 1px solid var(--outline);
  border-radius: 16px;
  background: var(--surface);
  overflow: hidden;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
`;

const TableScroll = styled.div`
  overflow-x: auto;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 0.8125rem;
  min-width: 720px;
`;

const Th = styled.th`
  text-align: left;
  padding: 0.85rem 1rem;
  font-size: 0.65rem;
  font-weight: 800;
  letter-spacing: 0.08em;
  color: var(--textSecondary, var(--onSurfaceVariant));
  text-transform: uppercase;
  border-bottom: 1px solid var(--outline);
  background: var(--surfaceVariant);
`;

const Td = styled.td`
  padding: 0.95rem 1rem;
  vertical-align: middle;
  border-bottom: 1px solid var(--outline);
`;

const StudentCell = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  min-width: 0;
`;

const AvatarRing = styled.div`
  width: 44px;
  height: 44px;
  border-radius: 999px;
  overflow: hidden;
  flex-shrink: 0;
  border: 2px solid var(--outline);
  background: var(--surfaceVariant);
`;

const AvatarImg = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
`;

const AvatarFallback = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, var(--primaryDark) 0%, var(--primary) 100%);
  color: var(--onPrimary);
  font-size: 0.8rem;
  font-weight: 700;
`;

const DataRow = styled.tr`
  &:hover td {
    background: color-mix(in srgb, var(--surfaceVariant) 65%, transparent);
  }
`;

const NameBlock = styled.div`
  min-width: 0;
`;

const StudentNameText = styled.div`
  font-weight: 700;
  font-size: 0.9rem;
  color: var(--text);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const StudentEmail = styled.div`
  font-size: 0.78rem;
  color: var(--textSecondary, var(--onSurfaceVariant));
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const GradeWrap = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const GradeBadge = styled.span<{ $variant: 'a' | 'b' | 'c' | 'd' | 'neutral' }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 2rem;
  padding: 0.25rem 0.45rem;
  border-radius: 8px;
  font-size: 0.75rem;
  font-weight: 800;
  ${({ $variant }) => {
    if ($variant === 'a')
      return `background: color-mix(in srgb, var(--success, #22a06b) 22%, transparent); color: var(--success, #4ade80); border: 1px solid color-mix(in srgb, var(--success, #22a06b) 40%, var(--outline));`;
    if ($variant === 'b')
      return `background: color-mix(in srgb, var(--primary) 20%, transparent); color: var(--primary); border: 1px solid color-mix(in srgb, var(--primary) 38%, var(--outline));`;
    if ($variant === 'c')
      return `background: color-mix(in srgb, var(--warning, #e8a23c) 22%, transparent); color: var(--warning, #fbbf24); border: 1px solid color-mix(in srgb, var(--warning, #e8a23c) 40%, var(--outline));`;
    if ($variant === 'd')
      return `background: color-mix(in srgb, var(--error, #dc2626) 22%, transparent); color: var(--error, #f87171); border: 1px solid color-mix(in srgb, var(--error, #dc2626) 40%, var(--outline));`;
    return `background: var(--surfaceVariant); color: var(--textSecondary, var(--onSurfaceVariant)); border: 1px solid var(--outline);`;
  }}
`;

const GradePct = styled.span`
  font-weight: 600;
  color: var(--text);
`;

const LastActiveText = styled.span`
  color: var(--textSecondary, var(--onSurfaceVariant));
  font-weight: 500;
`;

const EngagementCell = styled.div`
  min-width: 120px;
`;

const EngagementTop = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  margin-bottom: 0.35rem;
  font-size: 0.78rem;
  font-weight: 700;
  color: var(--text);
`;

const ProgressTrack = styled.div`
  height: 6px;
  border-radius: 999px;
  background: var(--surfaceVariant);
  overflow: hidden;
`;

const ProgressFill = styled.div<{ $pct: number; $low: boolean }>`
  height: 100%;
  width: ${({ $pct }) => Math.min(100, Math.max(0, $pct))}%;
  border-radius: 999px;
  background: ${({ $low }) => ($low ? 'var(--warning, #fb8c00)' : 'var(--primary)')};
`;

const ActionMenuWrap = styled.div`
  position: relative;
  display: inline-flex;
  justify-content: flex-end;
`;

const ActionMenuToggle = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: 10px;
  border: 1px solid var(--outline);
  background: var(--surface);
  color: var(--textSecondary, var(--onSurfaceVariant));
  cursor: pointer;
  &:hover:not(:disabled) {
    border-color: var(--primary);
    color: var(--primary);
  }
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const ActionMenu = styled.div`
  position: absolute;
  top: calc(100% + 4px);
  right: 0;
  z-index: 12;
  min-width: 188px;
  padding: 0.35rem;
  border-radius: 12px;
  border: 1px solid var(--outline);
  background: var(--surface);
  box-shadow: 0 10px 28px rgba(0, 0, 0, 0.16);
`;

const ActionMenuLink = styled(Link)`
  display: flex;
  width: 100%;
  align-items: center;
  padding: 0.55rem 0.7rem;
  border-radius: 8px;
  border: 0;
  background: transparent;
  color: var(--text);
  font-size: 0.8rem;
  font-weight: 700;
  font-family: inherit;
  text-decoration: none;
  text-align: left;
  cursor: pointer;
  &:hover {
    background: color-mix(in srgb, var(--surfaceVariant) 80%, transparent);
    color: var(--primary);
  }
`;

const ActionMenuItem = styled.button<{ $tone?: 'danger' }>`
  display: flex;
  width: 100%;
  align-items: center;
  padding: 0.55rem 0.7rem;
  border-radius: 8px;
  border: 0;
  background: transparent;
  color: ${({ $tone }) => ($tone === 'danger' ? 'var(--error, #dc2626)' : 'var(--text)')};
  font-size: 0.8rem;
  font-weight: 700;
  font-family: inherit;
  text-align: left;
  cursor: pointer;
  &:hover:not(:disabled) {
    background: ${({ $tone }) =>
      $tone === 'danger'
        ? 'color-mix(in srgb, var(--error, #dc2626) 12%, transparent)'
        : 'color-mix(in srgb, var(--surfaceVariant) 80%, transparent)'};
  }
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const LoadMoreWrap = styled.div`
  padding: 1.25rem 1rem 1.5rem;
  display: flex;
  justify-content: center;
  border-top: 1px solid var(--outline);
  background: var(--surface);
`;

const LoadMoreBtn = styled.button`
  padding: 0.65rem 2.5rem;
  border-radius: 12px;
  border: 1px solid var(--outline);
  background: var(--surface);
  font-size: 0.88rem;
  font-weight: 600;
  color: var(--text);
  cursor: pointer;
  font-family: inherit;
  &:hover {
    border-color: var(--primary);
    color: var(--primary);
    background: color-mix(in srgb, var(--primary) 10%, transparent);
  }
`;

const Empty = styled.p`
  margin: 0;
  padding: 2rem 1.25rem;
  border-radius: 16px;
  border: 1px dashed var(--outline);
  background: var(--surfaceVariant);
  font-size: 0.9rem;
  color: var(--textSecondary, var(--onSurfaceVariant));
  text-align: center;
`;

type StudentActivity = {
  submissions: number;
  pending: number;
  graded: number;
  average: number | null;
  lastSubmittedAt: string | null;
};

const PAGE_SIZE = 8;
const ACTIVE_WINDOW_MS = 1000 * 60 * 60 * 24 * 14;

function classCodeLabel(id: string, className: string): string {
  const short = id.replace(/-/g, '').slice(0, 6).toUpperCase();
  const topic = className.split(/\s+/).slice(0, 2).join(' ').toUpperCase();
  return `${topic || 'CLASS'} · ${short || 'NEW'}`;
}

function initials(name: string): string {
  const p = name.trim().split(/\s+/).filter(Boolean);
  if (p.length === 0) return '?';
  if (p.length === 1) return p[0]!.slice(0, 2).toUpperCase();
  return `${p[0]!.charAt(0)}${p[1]!.charAt(0)}`.toUpperCase();
}

const StudentAvatar: React.FC<{ fullName: string; avatarUrl?: string | null }> = ({ fullName, avatarUrl }) => {
  const [failed, setFailed] = useState(false);
  const url = typeof avatarUrl === 'string' ? avatarUrl.trim() : '';
  const showImg = Boolean(url) && !failed;

  return (
    <AvatarRing>
      {showImg ? (
        <AvatarImg
          src={url}
          alt=""
          loading="lazy"
          referrerPolicy="no-referrer"
          onError={() => setFailed(true)}
        />
      ) : (
        <AvatarFallback aria-hidden>{initials(fullName)}</AvatarFallback>
      )}
    </AvatarRing>
  );
};

function summarizeStudentActivity(rows: TeacherGradeRow[]): Map<string, StudentActivity> {
  const map = new Map<string, StudentActivity>();
  rows.forEach((row) => {
    const key = row.student_name.trim().toLowerCase();
    if (!key) return;
    const current =
      map.get(key) ??
      ({
        submissions: 0,
        pending: 0,
        graded: 0,
        average: null,
        lastSubmittedAt: null,
      } as StudentActivity);

    current.submissions += 1;
    if (row.status === 'submitted' || row.status === 'missing') current.pending += 1;
    if (row.status === 'graded' || row.status === 'returned') current.graded += 1;

    if (row.status === 'graded' || row.status === 'returned') {
      const maxPoints = row.max_points ?? 100;
      if (row.score != null && maxPoints > 0) {
        const pct = (row.score / maxPoints) * 100;
        current.average = current.average == null ? pct : (current.average + pct) / 2;
      }
    }

    const nextTs = row.submitted_at ? new Date(row.submitted_at).getTime() : Number.NaN;
    const prevTs = current.lastSubmittedAt ? new Date(current.lastSubmittedAt).getTime() : Number.NaN;
    if (!Number.isNaN(nextTs) && (Number.isNaN(prevTs) || nextTs > prevTs)) {
      current.lastSubmittedAt = row.submitted_at ?? null;
    }

    map.set(key, current);
  });
  return map;
}

function statusPriority(status: TeacherGradeRow['status']): number {
  if (status === 'graded' || status === 'returned') return 3;
  if (status === 'submitted') return 2;
  return 1;
}

function rowTimestamp(value?: string | null): number {
  if (!value) return 0;
  const ts = new Date(value).getTime();
  return Number.isNaN(ts) ? 0 : ts;
}

function resolveWorkKey(row: TeacherGradeRow): string | null {
  const workType = row.work_type ?? 'assignment';
  if (row.work_id?.trim()) return `${workType}:id:${row.work_id.trim()}`;
  const classKey = row.class_id?.trim() || row.class_name?.trim().toLowerCase() || '';
  const workTitle = row.work_title?.trim().toLowerCase() ?? '';
  if (!classKey || !workTitle) return null;
  return `${workType}:class:${classKey}:title:${workTitle}`;
}

function dedupeSubmissionRows(rows: TeacherGradeRow[]): TeacherGradeRow[] {
  const latestByStudentAndWork = new Map<string, TeacherGradeRow>();
  const passthrough: TeacherGradeRow[] = [];

  rows.forEach((row) => {
    const workKey = resolveWorkKey(row);
    const studentKey = row.student_id?.trim()
      ? `id:${row.student_id.trim()}`
      : row.student_name.trim()
        ? `name:${row.student_name.trim().toLowerCase()}`
        : '';
    if (!workKey || !studentKey) {
      passthrough.push(row);
      return;
    }
    const key = `${workKey}::${studentKey}`;
    const prev = latestByStudentAndWork.get(key);
    if (!prev) {
      latestByStudentAndWork.set(key, row);
      return;
    }
    const prevIsMissing = prev.status === 'missing';
    const currIsMissing = row.status === 'missing';
    if (prevIsMissing !== currIsMissing) {
      latestByStudentAndWork.set(key, currIsMissing ? prev : row);
      return;
    }
    const prevTs = rowTimestamp(prev.submitted_at);
    const currTs = rowTimestamp(row.submitted_at);
    if (currTs > prevTs) {
      latestByStudentAndWork.set(key, row);
      return;
    }
    if (currTs === prevTs && statusPriority(row.status) > statusPriority(prev.status)) {
      latestByStudentAndWork.set(key, row);
    }
  });

  return Array.from(latestByStudentAndWork)
    .map(([, row]) => row)
    .concat(passthrough);
}

function formatRelativeLastActive(iso: string | null): string {
  if (!iso) return '—';
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return '—';
  const diff = Date.now() - t;
  const sec = Math.floor(diff / 1000);
  if (sec < 60) return 'Just now';
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr} hour${hr === 1 ? '' : 's'} ago`;
  const day = Math.floor(hr / 24);
  if (day === 1) return 'Yesterday';
  if (day < 7) return `${day} days ago`;
  return new Date(iso).toLocaleDateString();
}

function percentToLetter(pct: number): { letter: string; variant: 'a' | 'b' | 'c' | 'd' | 'neutral' } {
  if (pct >= 93) return { letter: 'A', variant: 'a' };
  if (pct >= 90) return { letter: 'A-', variant: 'a' };
  if (pct >= 87) return { letter: 'B+', variant: 'b' };
  if (pct >= 83) return { letter: 'B', variant: 'b' };
  if (pct >= 80) return { letter: 'B-', variant: 'b' };
  if (pct >= 77) return { letter: 'C+', variant: 'c' };
  if (pct >= 73) return { letter: 'C', variant: 'c' };
  if (pct >= 70) return { letter: 'C-', variant: 'c' };
  if (pct >= 67) return { letter: 'D+', variant: 'd' };
  if (pct >= 60) return { letter: 'D', variant: 'd' };
  return { letter: 'F', variant: 'd' };
}

function engagementScore(activity: StudentActivity): number {
  if (activity.submissions === 0) return 0;
  const rate = activity.graded / activity.submissions;
  const base = Math.round(rate * 100);
  if (activity.average != null) {
    return Math.min(100, Math.round(base * 0.45 + activity.average * 0.55));
  }
  return base;
}

function downloadRosterCsv(
  className: string,
  students: { full_name: string; email: string | null }[],
  activityByKey: Map<string, StudentActivity>
): void {
  const lines = ['Name,Email,Grade %,Engagement,Last active'];
  students.forEach((s) => {
    const act = activityByKey.get(s.full_name.trim().toLowerCase());
    const pct = act?.average != null ? String(Math.round(act.average)) : '';
    const eng = act ? String(engagementScore(act)) : '';
    const last = act?.lastSubmittedAt ?? '';
    const esc = (v: string) => `"${v.replace(/"/g, '""')}"`;
    lines.push([esc(s.full_name), esc(s.email ?? ''), pct, eng, esc(last)].join(','));
  });
  const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `${className.replace(/[^\w\s-]/g, '').trim() || 'class'}-roster.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

const ClassroomTeacherClassStudentsPage: React.FC = () => {
  const { classId = '' } = useParams<{ classId: string }>();
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const [filterOpen, setFilterOpen] = useState(false);
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
  const [enrollPanelOpen, setEnrollPanelOpen] = useState(false);
  const [enrollSearch, setEnrollSearch] = useState('');
  const [selectedStudentIds, setSelectedStudentIds] = useState<string[]>([]);
  const [enrollError, setEnrollError] = useState<string | null>(null);
  const [enrollSuccess, setEnrollSuccess] = useState<string | null>(null);
  const [rosterError, setRosterError] = useState<string | null>(null);
  const [rosterSuccess, setRosterSuccess] = useState<string | null>(null);
  const [actionsMenuStudentId, setActionsMenuStudentId] = useState<string | null>(null);
  const actionsMenuRef = useRef<HTMLDivElement>(null);

  const studentDirectoryQuery = useQuery({
    queryKey: ['classroom', 'teacher', 'students'],
    queryFn: teacherService.getStudentOptions,
  });

  const enrollStudentsMutation = useMutation({
    mutationFn: teacherService.enrollStudents,
    onSuccess: async (result) => {
      if (!result.ok) {
        setEnrollError(result.error);
        setEnrollSuccess(null);
        return;
      }
      setEnrollError(null);
      setEnrollSuccess(
        `Added ${result.addedCount} student${result.addedCount === 1 ? '' : 's'} to this class.`
      );
      setSelectedStudentIds([]);
      setEnrollSearch('');
      await queryClient.invalidateQueries({ queryKey: ['classroom', 'teacher', 'class-students', classId] });
      await queryClient.invalidateQueries({ queryKey: ['classroom', 'teacher', 'overview'] });
      await queryClient.invalidateQueries({ queryKey: ['classroom', 'teacher', 'activitySummary'] });
    },
    onError: (error: unknown) => {
      const message = error instanceof Error ? error.message : 'Unable to add students.';
      setEnrollError(message);
      setEnrollSuccess(null);
    },
  });

  const unenrollStudentsMutation = useMutation({
    mutationFn: teacherService.unenrollStudents,
    onSuccess: async (result) => {
      if (!result.ok) {
        setRosterError(result.error);
        setRosterSuccess(null);
        return;
      }
      setRosterError(null);
      setRosterSuccess(
        `Removed ${result.removedCount} student${result.removedCount === 1 ? '' : 's'} from this class.`
      );
      setActionsMenuStudentId(null);
      await queryClient.invalidateQueries({ queryKey: ['classroom', 'teacher', 'class-students', classId] });
      await queryClient.invalidateQueries({ queryKey: ['classroom', 'teacher', 'overview'] });
      await queryClient.invalidateQueries({ queryKey: ['classroom', 'teacher', 'activitySummary'] });
    },
    onError: (error: unknown) => {
      const message = error instanceof Error ? error.message : 'Unable to remove student.';
      setRosterError(message);
      setRosterSuccess(null);
    },
  });

  const [overviewQuery, classStudentsQuery, gradesQuery, lessonsQuery, assignmentsQuery, quizzesQuery] = useQueries({
    queries: [
      { queryKey: ['classroom', 'teacher', 'overview'], queryFn: teacherService.getOverview },
      { queryKey: ['classroom', 'teacher', 'class-students', classId], queryFn: () => teacherService.getClassStudents(classId) },
      { queryKey: ['classroom', 'teacher', 'grades'], queryFn: teacherService.getGrades },
      { queryKey: ['classroom', 'teacher', 'lessons'], queryFn: teacherService.getLessons },
      { queryKey: ['classroom', 'teacher', 'work', 'assignment'], queryFn: () => teacherService.getWork('assignment') },
      { queryKey: ['classroom', 'teacher', 'work', 'quiz'], queryFn: () => teacherService.getWork('quiz') },
    ],
  });

  const classStudents = useMemo(() => classStudentsQuery.data ?? [], [classStudentsQuery.data]);
  const enrolledStudentIds = useMemo(() => new Set(classStudents.map((student) => student.id)), [classStudents]);
  const availableStudents = useMemo(() => {
    const rows = studentDirectoryQuery.data ?? [];
    return rows.filter((student) => !enrolledStudentIds.has(student.id));
  }, [studentDirectoryQuery.data, enrolledStudentIds]);

  useEffect(() => {
    const availableSet = new Set(availableStudents.map((student) => student.id));
    setSelectedStudentIds((prev) => prev.filter((id) => availableSet.has(id)));
  }, [availableStudents]);

  useEffect(() => {
    setVisibleCount(PAGE_SIZE);
  }, [search, classId]);

  useEffect(() => {
    setActionsMenuStudentId(null);
    setRosterError(null);
    setRosterSuccess(null);
  }, [classId]);

  useEffect(() => {
    if (!actionsMenuStudentId) return;
    const onPointerDown = (event: MouseEvent) => {
      if (actionsMenuRef.current?.contains(event.target as Node)) return;
      setActionsMenuStudentId(null);
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setActionsMenuStudentId(null);
    };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [actionsMenuStudentId]);

  if (
    overviewQuery.isLoading ||
    classStudentsQuery.isLoading ||
    gradesQuery.isLoading ||
    lessonsQuery.isLoading ||
    assignmentsQuery.isLoading ||
    quizzesQuery.isLoading
  ) {
    return <Empty>Loading class students…</Empty>;
  }
  if (
    overviewQuery.isError ||
    classStudentsQuery.isError ||
    gradesQuery.isError ||
    lessonsQuery.isError ||
    assignmentsQuery.isError ||
    quizzesQuery.isError
  ) {
    return <Empty>Unable to load class students.</Empty>;
  }

  const classItem = (overviewQuery.data ?? []).find((row) => row.id === classId);
  if (!classItem) {
    return (
      <Page>
        <BackRow>
          <BackLink to="/classroom-teacher/classes">
            <FaArrowLeft size={12} aria-hidden />
            Back to Classes
          </BackLink>
        </BackRow>
        <Empty>Class not found.</Empty>
      </Page>
    );
  }

  const classRows = dedupeSubmissionRows(
    (gradesQuery.data ?? []).filter(
      (row) => row.class_id === classId || (!row.class_id && row.class_name === classItem.name)
    )
  );
  const studentActivityByName = summarizeStudentActivity(classRows);
  const classAssignments = (assignmentsQuery.data ?? []).filter((row) => row.class_id === classId);
  const classQuizzes = (quizzesQuery.data ?? []).filter((row) => row.class_id === classId);
  const shortClassName = classItem.name.split(/\s+/).slice(0, 4).join(' ') || 'This class';

  const q = search.trim().toLowerCase();
  const filteredStudents = !q
    ? classStudents
    : classStudents.filter(
        (s) =>
          s.full_name.toLowerCase().includes(q) ||
          (s.email ?? '').toLowerCase().includes(q)
      );

  const now = Date.now();
  const activeCount = classStudents.filter((s) => {
    const act = studentActivityByName.get(s.full_name.trim().toLowerCase());
    if (!act?.lastSubmittedAt) return false;
    const t = new Date(act.lastSubmittedAt).getTime();
    return !Number.isNaN(t) && now - t <= ACTIVE_WINDOW_MS;
  }).length;

  const gradePercents: number[] = [];
  classStudents.forEach((s) => {
    const act = studentActivityByName.get(s.full_name.trim().toLowerCase());
    if (act?.average != null) gradePercents.push(act.average);
  });
  const classAveragePct =
    gradePercents.length === 0 ? null : Math.round(gradePercents.reduce((a, b) => a + b, 0) / gradePercents.length);

  const enrollSearchQuery = enrollSearch.trim().toLowerCase();
  const filteredAvailableStudents = !enrollSearchQuery
    ? availableStudents
    : availableStudents.filter((student) =>
        `${student.full_name} ${student.email ?? ''}`.toLowerCase().includes(enrollSearchQuery)
      );

  const visibleSlice = filteredStudents.slice(0, visibleCount);
  const hasMore = filteredStudents.length > visibleSlice.length;

  const toggleSelectedStudent = (studentId: string) => {
    setSelectedStudentIds((prev) => (prev.includes(studentId) ? prev.filter((id) => id !== studentId) : [...prev, studentId]));
  };

  const handleAddStudents = () => {
    if (!classId.trim() || selectedStudentIds.length === 0 || enrollStudentsMutation.isPending) return;
    setEnrollError(null);
    setEnrollSuccess(null);
    enrollStudentsMutation.mutate({
      classId: classId.trim(),
      studentIds: selectedStudentIds,
    });
  };

  const handleRemoveStudent = (student: { id: string; full_name: string }) => {
    if (!classId.trim() || unenrollStudentsMutation.isPending) return;
    const confirmed = window.confirm(
      `Remove ${student.full_name} from this class? They will lose access to this class.`
    );
    if (!confirmed) return;
    setRosterError(null);
    setRosterSuccess(null);
    setActionsMenuStudentId(null);
    unenrollStudentsMutation.mutate({
      classId: classId.trim(),
      studentIds: [student.id],
    });
  };

  const pendingRemoveStudentIds = new Set(
    unenrollStudentsMutation.isPending ? (unenrollStudentsMutation.variables?.studentIds ?? []) : []
  );

  return (
    <Page>
      <BackRow>
        <BackLink to="/classroom-teacher/classes">
          <FaArrowLeft size={12} aria-hidden />
          Back to Classes
        </BackLink>
      </BackRow>

      <CourseBanner $imageUrl={classItem.class_image_url}>
        <BannerKicker>{classCodeLabel(classId, classItem.name)}</BannerKicker>
        <BannerTitle>{classItem.name}</BannerTitle>
        <BannerSub>
          Class workspace · {classAssignments.length + classQuizzes.length} assignment
          {classAssignments.length + classQuizzes.length === 1 ? '' : 's'} &amp; quizzes
        </BannerSub>
      </CourseBanner>

      <LayoutGrid>
        <ClassSectionSidebar classId={classId} className={shortClassName} subLabel="Section · class">
          <RosterCard>
            <RosterLabel>Total students</RosterLabel>
            <RosterValue>{classStudents.length}</RosterValue>
            <DownloadRosterBtn
              type="button"
              onClick={() => downloadRosterCsv(classItem.name, classStudents, studentActivityByName)}
              disabled={classStudents.length === 0}
            >
              <FaDownload size={12} aria-hidden />
              Download roster
            </DownloadRosterBtn>
          </RosterCard>
        </ClassSectionSidebar>

        <MainCol>
          <ToolbarRow>
            <SearchWrap>
              <FaSearch size={14} aria-hidden style={{ color: 'var(--textSecondary, #64748b)', flexShrink: 0 }} />
              <SearchInput
                placeholder="Search students…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                aria-label="Search students"
              />
            </SearchWrap>
            <IconToolBtn type="button" aria-label="Filter" aria-expanded={filterOpen} onClick={() => setFilterOpen((v) => !v)}>
              <FaFilter size={14} />
            </IconToolBtn>
            <MessageAllBtn
              to={`/classroom-teacher/communication?classId=${encodeURIComponent(classId)}&mode=broadcast`}
              aria-disabled={classStudents.length === 0}
            >
              <FaPaperPlane size={12} aria-hidden />
              Message all students
            </MessageAllBtn>
            <AddStudentsBtn
              type="button"
              aria-expanded={enrollPanelOpen}
              onClick={() => {
                setEnrollPanelOpen((prev) => !prev);
                setEnrollError(null);
                setEnrollSuccess(null);
              }}
            >
              <FaPlus size={12} aria-hidden />
              Add students
            </AddStudentsBtn>
            <PillsRow>
              <SummaryPill $tone="primary">
                Active students: {activeCount}/{classStudents.length || 0}
              </SummaryPill>
              <SummaryPill $tone="warning">
                Class average: {classAveragePct != null ? `${classAveragePct}%` : '—'}
              </SummaryPill>
            </PillsRow>
          </ToolbarRow>
          {filterOpen ? (
            <Empty style={{ padding: '0.65rem 1rem', textAlign: 'left', marginTop: '-0.35rem' }}>
              Filters coming soon — use search for now.
            </Empty>
          ) : null}
          {enrollPanelOpen ? (
            <EnrollPanel>
              <EnrollPanelTop>
                <EnrollPanelTitle>Add students to this class</EnrollPanelTitle>
                <EnrollCount>
                  {availableStudents.length} available · {selectedStudentIds.length} selected
                </EnrollCount>
              </EnrollPanelTop>

              <SearchWrap>
                <FaSearch size={14} aria-hidden style={{ color: 'var(--textSecondary, #64748b)', flexShrink: 0 }} />
                <SearchInput
                  placeholder="Search students to enroll…"
                  value={enrollSearch}
                  onChange={(e) => setEnrollSearch(e.target.value)}
                  aria-label="Search students to enroll"
                />
              </SearchWrap>

              {enrollError ? <InlineStatus $tone="error">{enrollError}</InlineStatus> : null}
              {enrollSuccess ? <InlineStatus $tone="success">{enrollSuccess}</InlineStatus> : null}

              <CandidateList>
                {studentDirectoryQuery.isLoading ? (
                  <Empty style={{ border: 0, borderRadius: 0, margin: 0, background: 'transparent' }}>Loading student directory…</Empty>
                ) : studentDirectoryQuery.isError ? (
                  <Empty style={{ border: 0, borderRadius: 0, margin: 0, background: 'transparent' }}>
                    Unable to load student directory right now.
                  </Empty>
                ) : availableStudents.length === 0 ? (
                  <Empty style={{ border: 0, borderRadius: 0, margin: 0, background: 'transparent' }}>
                    All available students are already enrolled.
                  </Empty>
                ) : filteredAvailableStudents.length === 0 ? (
                  <Empty style={{ border: 0, borderRadius: 0, margin: 0, background: 'transparent' }}>No students match your search.</Empty>
                ) : (
                  filteredAvailableStudents.map((student) => {
                    const selected = selectedStudentIds.includes(student.id);
                    return (
                      <CandidateRow key={student.id}>
                        <CandidateMain>
                          <StudentAvatar fullName={student.full_name} avatarUrl={student.avatar_url} />
                          <div style={{ minWidth: 0 }}>
                            <CandidateName>{student.full_name}</CandidateName>
                            <CandidateEmail>{student.email ?? 'No email on file'}</CandidateEmail>
                          </div>
                        </CandidateMain>
                        <CandidateToggleBtn
                          type="button"
                          $selected={selected}
                          onClick={() => toggleSelectedStudent(student.id)}
                          aria-label={selected ? `Remove ${student.full_name} from selection` : `Select ${student.full_name}`}
                        >
                          {selected ? <FaCheck size={14} aria-hidden /> : <FaPlus size={13} aria-hidden />}
                        </CandidateToggleBtn>
                      </CandidateRow>
                    );
                  })
                )}
              </CandidateList>

              <EnrollActions>
                <EnrollSubmitBtn
                  type="button"
                  onClick={handleAddStudents}
                  disabled={selectedStudentIds.length === 0 || enrollStudentsMutation.isPending}
                >
                  {enrollStudentsMutation.isPending
                    ? 'Adding…'
                    : `Add ${selectedStudentIds.length} student${selectedStudentIds.length === 1 ? '' : 's'}`}
                </EnrollSubmitBtn>
              </EnrollActions>
            </EnrollPanel>
          ) : null}

          {rosterError ? <InlineStatus $tone="error">{rosterError}</InlineStatus> : null}
          {rosterSuccess ? <InlineStatus $tone="success">{rosterSuccess}</InlineStatus> : null}

          {classStudents.length === 0 ? (
            <Empty>No students are enrolled in this class yet.</Empty>
          ) : filteredStudents.length === 0 ? (
            <Empty>No students match your search.</Empty>
          ) : (
            <TableCard style={actionsMenuStudentId ? { overflow: 'visible' } : undefined}>
              <TableScroll style={actionsMenuStudentId ? { overflow: 'visible' } : undefined}>
                <Table>
                  <thead>
                    <tr>
                      <Th>Student name</Th>
                      <Th>Current grade</Th>
                      <Th>Last active</Th>
                      <Th>Engagement</Th>
                      <Th style={{ textAlign: 'right' }}>Actions</Th>
                    </tr>
                  </thead>
                  <tbody>
                    {visibleSlice.map((student) => {
                      const activity = studentActivityByName.get(student.full_name.trim().toLowerCase()) ?? {
                        submissions: 0,
                        pending: 0,
                        graded: 0,
                        average: null,
                        lastSubmittedAt: null,
                      };
                      const eng = engagementScore(activity);
                      const lowEng = eng < 70;
                      const gradePct = activity.average != null ? Math.round(activity.average) : null;
                      const { letter, variant } =
                        gradePct != null ? percentToLetter(gradePct) : { letter: '—', variant: 'neutral' as const };
                      const menuOpen = actionsMenuStudentId === student.id;
                      const removing = pendingRemoveStudentIds.has(student.id);

                      return (
                        <DataRow key={student.id}>
                          <Td>
                            <StudentCell>
                              <StudentAvatar fullName={student.full_name} avatarUrl={student.avatar_url} />
                              <NameBlock>
                                <StudentNameText>{student.full_name}</StudentNameText>
                                <StudentEmail>{student.email ?? 'No email on file'}</StudentEmail>
                              </NameBlock>
                            </StudentCell>
                          </Td>
                          <Td>
                            <GradeWrap>
                              <GradeBadge $variant={variant}>{letter}</GradeBadge>
                              {gradePct != null ? <GradePct>{gradePct}%</GradePct> : <GradePct>—</GradePct>}
                            </GradeWrap>
                          </Td>
                          <Td>
                            <LastActiveText>{formatRelativeLastActive(activity.lastSubmittedAt)}</LastActiveText>
                          </Td>
                          <Td>
                            <EngagementCell>
                              <EngagementTop>
                                <span>{eng}/100</span>
                              </EngagementTop>
                              <ProgressTrack>
                                <ProgressFill $pct={eng} $low={lowEng} />
                              </ProgressTrack>
                            </EngagementCell>
                          </Td>
                          <Td style={{ textAlign: 'right' }}>
                            <ActionMenuWrap ref={menuOpen ? actionsMenuRef : undefined}>
                              <ActionMenuToggle
                                type="button"
                                title="Student actions"
                                aria-label={`Actions for ${student.full_name}`}
                                aria-haspopup="menu"
                                aria-expanded={menuOpen}
                                disabled={removing}
                                onClick={() =>
                                  setActionsMenuStudentId((current) => (current === student.id ? null : student.id))
                                }
                              >
                                <FaEllipsisH size={14} />
                              </ActionMenuToggle>
                              {menuOpen ? (
                                <ActionMenu role="menu" aria-label={`Actions for ${student.full_name}`}>
                                  <ActionMenuLink
                                    role="menuitem"
                                    to={`/admin/members/${encodeURIComponent(student.id)}`}
                                    onClick={() => setActionsMenuStudentId(null)}
                                  >
                                    View profile
                                  </ActionMenuLink>
                                  <ActionMenuItem
                                    type="button"
                                    role="menuitem"
                                    $tone="danger"
                                    disabled={removing}
                                    onClick={() => handleRemoveStudent(student)}
                                  >
                                    {removing ? 'Removing…' : 'Remove from class'}
                                  </ActionMenuItem>
                                </ActionMenu>
                              ) : null}
                            </ActionMenuWrap>
                          </Td>
                        </DataRow>
                      );
                    })}
                  </tbody>
                </Table>
              </TableScroll>
              {hasMore ? (
                <LoadMoreWrap>
                  <LoadMoreBtn type="button" onClick={() => setVisibleCount((c) => c + PAGE_SIZE)}>
                    Load more students
                  </LoadMoreBtn>
                </LoadMoreWrap>
              ) : null}
            </TableCard>
          )}
        </MainCol>
      </LayoutGrid>
    </Page>
  );
};

export default ClassroomTeacherClassStudentsPage;
